import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import PartnerShell from '../components/PartnerShell';
import { getPartnerOrders, getPartnerWallet } from '../partnerApi';
import { COLOR, glass } from '../theme';

const fmt$ = (n) => '$' + (n || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

const fmtTime = (ts) => {
  if (!ts) return '—';
  const d = new Date(ts);
  const diff = (Date.now() - d) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  return d.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: true });
};

const fmtItems = (items) => {
  if (!Array.isArray(items)) return '—';
  return items.map((i) => `${i.name}${i.qty > 1 ? ` ×${i.qty}` : ''}`).join(', ') || '—';
};

const STATUS_META = {
  new: { label: 'New', color: '#F59E0B' },
  preparing: { label: 'Preparing', color: '#38BDF8' },
  ready: { label: 'Ready', color: '#4ADE80' },
  complete: { label: 'Complete', color: '#6B7280' },
  completed: { label: 'Complete', color: '#6B7280' },
  rejected: { label: 'Rejected', color: '#EF4444' },
  refunded: { label: 'Refunded', color: '#EF4444' },
};

export default function PartnerDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [available, setAvailable] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const [orders, wallet] = await Promise.all([getPartnerOrders(), getPartnerWallet()]);
      setData(orders);
      setAvailable(wallet.balance.available);
      setError('');
    } catch (e) {
      setError(e.message || 'Could not load your dashboard');
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const counts = data ? data.counts : { new: 0, preparing: 0, ready: 0, completed: 0 };

  return (
    <PartnerShell>
      <h1 style={S.h1}>Today</h1>
      <p style={S.sub}>Orders placed by hotel guests today.</p>

      {error && <div style={S.error}>{error}</div>}

      <div style={S.tiles}>
        {[
          { label: 'New', value: counts.new, color: '#F59E0B' },
          { label: 'Preparing', value: counts.preparing, color: '#38BDF8' },
          { label: 'Ready', value: counts.ready, color: '#4ADE80' },
          { label: 'Revenue', value: data ? fmt$(data.revenue) : '—', color: COLOR.text },
        ].map((t, i) => (
          <motion.div
            key={t.label}
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.06 }}
            style={S.tile}
          >
            <div style={{ ...S.tileValue, color: t.color }}>{t.value}</div>
            <div style={S.tileLabel}>{t.label}</div>
          </motion.div>
        ))}
      </div>

      <div style={S.walletStrip} onClick={() => navigate('/partner/wallet')}>
        <div>
          <div style={S.walletLabel}>Available to cash out</div>
          <div style={S.walletValue}>{available == null ? '—' : fmt$(available)}</div>
        </div>
        <span style={S.walletArrow}>›</span>
      </div>

      <h2 style={S.h2}>Order feed</h2>
      {!data ? (
        <div style={S.empty}>Loading…</div>
      ) : data.orders.length === 0 ? (
        <div style={S.empty}>No orders yet today.</div>
      ) : (
        <div style={S.list}>
          {data.orders.map((o, i) => {
            const status = STATUS_META[o.status] || STATUS_META.new;
            return (
              <motion.div
                key={o.orderId || i}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: Math.min(i * 0.03, 0.3) }}
                style={S.row}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={S.rowTop}>
                    <span style={S.room}>Room {o.roomNumber || '—'}</span>
                    <span style={{ ...S.badge, color: status.color, background: `${status.color}1f` }}>
                      {status.label}
                    </span>
                  </div>
                  <div style={S.items} title={fmtItems(o.items)}>{fmtItems(o.items)}</div>
                </div>
                <div style={S.rowRight}>
                  <div style={S.amount}>{fmt$(parseFloat(o.total))}</div>
                  <div style={S.time}>{fmtTime(o.createdAt)}</div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </PartnerShell>
  );
}

const S = {
  h1: { fontSize: '28px', fontWeight: '800', letterSpacing: '-0.8px', margin: '0 0 4px' },
  sub: { fontSize: '14px', color: COLOR.textMuted, margin: '0 0 24px' },
  h2: { fontSize: '16px', fontWeight: '700', letterSpacing: '-0.3px', margin: '32px 0 12px' },
  error: {
    ...glass({ borderRadius: '14px', padding: '14px 16px' }),
    borderColor: 'rgba(248,113,113,0.3)', color: '#f87171', fontSize: '13.5px', marginBottom: '20px',
  },
  tiles: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px' },
  tile: { ...glass({ borderRadius: '16px', padding: '16px 12px' }), textAlign: 'center' },
  tileValue: { fontSize: '24px', fontWeight: '800', letterSpacing: '-0.8px', lineHeight: 1 },
  tileLabel: { fontSize: '11px', color: COLOR.textFaint, marginTop: '6px', fontWeight: '600' },
  walletStrip: {
    ...glass({ borderRadius: '18px', padding: '18px 20px' }),
    marginTop: '12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    cursor: 'pointer',
  },
  walletLabel: { fontSize: '12px', color: COLOR.textFaint, fontWeight: '600', letterSpacing: '0.3px' },
  walletValue: { fontSize: '26px', fontWeight: '800', letterSpacing: '-0.8px', marginTop: '4px', color: '#4ADE80' },
  walletArrow: { fontSize: '26px', color: COLOR.textFaint, fontWeight: '300' },
  list: { display: 'flex', flexDirection: 'column', gap: '10px' },
  row: { ...glass({ borderRadius: '16px', padding: '14px 16px' }), display: 'flex', alignItems: 'center', gap: '14px' },
  rowTop: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '5px' },
  room: { fontSize: '14px', fontWeight: '700' },
  badge: { fontSize: '10.5px', fontWeight: '700', borderRadius: '20px', padding: '3px 9px' },
  items: {
    fontSize: '12.5px', color: COLOR.textMuted, overflow: 'hidden',
    textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  rowRight: { textAlign: 'right', flexShrink: 0 },
  amount: { fontSize: '15px', fontWeight: '700' },
  time: { fontSize: '11.5px', color: COLOR.textFaint, marginTop: '3px' },
  empty: {
    ...glass({ borderRadius: '18px', padding: '40px 20px' }),
    textAlign: 'center', fontSize: '14px', color: COLOR.textFaint,
  },
};
